/**
 * 字段权限策略配置 — §3.2 权限矩阵的前端单一来源
 *
 * 每个字段声明：需要几级数据权限、脱敏格式、是否跳过中间脱敏态直接锁定。
 * 页面代码只需调用 sf(value, 'spend', userLevel)，无需关心具体等级和格式。
 *
 * 数据等级对应角色（后端 roleDataLevel，前端读 adContext.data_level）：
 *   L1 = 1 (公开)    — 所有人可见
 *   L2 = 2 (内部)    — Optimizer(2) 及以上
 *   L3 = 3 (机密)    — Lead(3) 及以上
 *   L4 = 4 (受限)    — 即使 Admin 也脱敏
 *
 * alwaysLock = true：缺少权限时直接锁定，不走脱敏中间态
 *   适用于毛利率、盈亏额等不能给出任何数量级提示的字段
 * alwaysLock = false（默认）：缺一级 → 脱敏；缺两级及以上 → 锁定
 */

import { securityFilter, type DataLevel, type FieldType, type FilterResult } from './securityFilter'

export type FieldKey =
  // L2 内部数据 ─────────────────────────────────────────────────────
  | 'spend'           // 花费（日/周）
  | 'revenue'         // 收入
  | 'budget'          // 预算
  | 'spend_cap'       // 投放上限
  | 'cpc'             // 单次点击成本
  | 'cpm'             // 千次展示成本
  | 'cpa'             // 单次转化成本
  | 'roas'            // 广告支出回报率
  | 'account_id'      // 广告账户 ID
  | 'knowledge_bank'  // 经验库（alwaysLock: Viewer 直接锁定，不走脱敏）
  // L3 机密数据 ─────────────────────────────────────────────────────
  | 'profit'          // 盈亏额（alwaysLock: Optimizer 也锁定）
  | 'margin'          // 毛利率（alwaysLock: Optimizer 也锁定）
  | 'product_cost'    // 产品成本（alwaysLock: Optimizer 也锁定）
  | 'break_even_cpa'  // 盈亏平衡 CPA（alwaysLock: Optimizer 也锁定）
  | 'monthly_spend'   // 月总花费（Optimizer 脱敏，Viewer 锁定）
  | 'refund'          // 退款额（Optimizer 脱敏，Viewer 锁定）
  | 'refund_rate'     // 退款率（Optimizer 脱敏，Viewer 锁定）
  | 'shipping'        // 物流成本（Optimizer 脱敏，Viewer 锁定）
  // L4 受限数据 ─────────────────────────────────────────────────────
  | 'api_token'       // API Token（所有人脱敏，包括 Admin）
  | 'payment_card'    // 付款卡（所有人脱敏，包括 Admin）

export interface FieldPolicy {
  level: DataLevel
  fieldType: FieldType
  /** true → 缺少权限直接锁定，不走脱敏中间态 */
  alwaysLock?: boolean
  label: string
}

/**
 * 字段策略表 — 对应 §3.2 权限矩阵
 *
 * 矩阵效果（由 level + alwaysLock 共同决定）：
 *
 * L2 普通字段（spend/revenue/budget/cpc/cpm/cpa/roas/account_id）：
 *   Viewer(1) → 脱敏   Optimizer(2)+ → 明文
 *
 * L2 + alwaysLock（knowledge_bank）：
 *   Viewer(1) → 锁定   Optimizer(2)+ → 明文
 *
 * L3 + alwaysLock（profit/margin/product_cost/break_even_cpa）：
 *   Viewer(1) → 锁定   Optimizer(2) → 锁定   Lead(3)+ → 明文
 *
 * L3 普通字段（monthly_spend/refund/refund_rate/shipping）：
 *   Viewer(1) → 锁定   Optimizer(2) → 脱敏   Lead(3)+ → 明文
 *
 * L4 字段（api_token/payment_card）：
 *   Lead(3)- → 锁定   Admin(4) → 脱敏（fieldType 决定格式）
 */
export const FIELD_POLICY: Record<FieldKey, FieldPolicy> = {
  // ── L2 内部数据 ────────────────────────────────────────────────────
  spend:          { level: 'L2', fieldType: 'money',   label: '花费' },
  revenue:        { level: 'L2', fieldType: 'money',   label: '收入' },
  budget:         { level: 'L2', fieldType: 'money',   label: '预算' },
  spend_cap:      { level: 'L2', fieldType: 'money',   label: '投放上限' },
  cpc:            { level: 'L2', fieldType: 'money',   label: 'CPC' },
  cpm:            { level: 'L2', fieldType: 'money',   label: 'CPM' },
  cpa:            { level: 'L2', fieldType: 'money',   label: 'CPA' },
  roas:           { level: 'L2', fieldType: 'default', label: 'ROAS' },
  account_id:     { level: 'L2', fieldType: 'id',      label: '广告账户 ID' },
  // 经验库：L2 但 Viewer 直接锁定（不走脱敏，避免泄露内容存在感）
  knowledge_bank: { level: 'L2', fieldType: 'default', alwaysLock: true, label: '经验库' },

  // ── L3 机密数据 ────────────────────────────────────────────────────
  profit:         { level: 'L3', fieldType: 'money',   alwaysLock: true, label: '盈亏额' },
  margin:         { level: 'L3', fieldType: 'percent', alwaysLock: true, label: '毛利率' },
  product_cost:   { level: 'L3', fieldType: 'money',   alwaysLock: true, label: '产品成本' },
  break_even_cpa: { level: 'L3', fieldType: 'money',   alwaysLock: true, label: '盈亏平衡 CPA' },
  // 月总花费：L3 但 Optimizer 可看脱敏版（知道量级，不知道精确值）
  monthly_spend:  { level: 'L3', fieldType: 'money',   label: '月总花费' },
  refund:         { level: 'L3', fieldType: 'money',   label: '退款额' },
  refund_rate:    { level: 'L3', fieldType: 'percent', label: '退款率' },
  shipping:       { level: 'L3', fieldType: 'money',   label: '物流成本' },

  // ── L4 受限数据 ────────────────────────────────────────────────────
  api_token:      { level: 'L4', fieldType: 'token',   label: 'API Token' },
  payment_card:   { level: 'L4', fieldType: 'card',    label: '付款卡信息' },
}

/**
 * 按字段策略过滤显示值
 *
 * 替代页面中散落的 securityFilter(v, 'L2', userLevel, 'money') 调用。
 * 字段的等级、格式、alwaysLock 规则均从 FIELD_POLICY 读取。
 *
 * @example
 *   sf(spend, 'spend', userLevel).value ?? '$*'
 *   sf(margin, 'margin', userLevel).value ?? '🔒'
 */
export function sf(value: string, key: FieldKey, userLevel: number): FilterResult {
  const p = FIELD_POLICY[key]
  return securityFilter(value, p.level, userLevel, p.fieldType, p.alwaysLock)
}
